import { Quill } from 'react-quill'
import type ReactQuill from 'react-quill'
import type { RefObject } from 'react'

/**
 * Shared react-quill configuration for every note composer in the app.
 *
 * This exists as its own module for a specific reason: registering the camera
 * icon mutates Quill's GLOBAL icon registry. Two modules doing that
 * independently at import time makes behaviour depend on module load order. One
 * module imported many times registers exactly once.
 */

const icons = Quill.import('ui/icons')
icons['camera'] =
	'<svg viewBox="0 0 18 18"><circle class="ql-fill" cx="9" cy="9" r="3"/><rect class="ql-stroke" height="12" width="12" x="3" y="3" rx="2" ry="2"/><line class="ql-stroke" x1="12" x2="12" y1="6" y2="6"/></svg>'

export type QuillRef = RefObject<ReactQuill | null>

/**
 * Insert one or more picked files into the editor as base64 <img> embeds.
 *
 * Base64 at compose time is intentional and matches the existing pipeline:
 * nothing is uploaded until the note is submitted, at which point
 * `convertRTEImages` extracts the embeds and `modifyNote` swaps them for
 * Cloudinary URLs. It keeps an abandoned draft from leaving orphaned assets.
 */
const insertFilesAsEmbeds = (quillRef: QuillRef, files: File[]) => {
	files.forEach(file => {
		const reader = new FileReader()
		reader.onload = e => {
			const quill = quillRef.current?.getEditor()
			if (!quill) return
			const range = quill.getSelection(true)
			if (!range) return
			quill.insertEmbed(range.index, 'image', e.target?.result)
			quill.setSelection(range.index + 1, 0)
		}
		reader.readAsDataURL(file)
	})
}

/** Opens a file picker and embeds whatever is chosen. */
export const openPicker = (
	quillRef: QuillRef,
	{ multiple, capture }: { multiple: boolean; capture?: string },
) => {
	const input = document.createElement('input')
	input.setAttribute('type', 'file')
	input.setAttribute('accept', 'image/*')
	if (multiple) input.setAttribute('multiple', '')
	if (capture) input.setAttribute('capture', capture)
	input.click()

	input.onchange = () => {
		insertFilesAsEmbeds(quillRef, Array.from(input.files || []))
	}
}

export const createCameraHandler = (quillRef: QuillRef) => () =>
	openPicker(quillRef, { multiple: false, capture: 'environment' })

/**
 * The toolbar's image button.
 *
 * With no `onChooseSource` it does what it always did: straight to a file
 * picker. A composer that can also offer the image library passes one, and the
 * button asks where the picture should come from first.
 */
export const createImageHandler = (quillRef: QuillRef, onChooseSource?: () => void) => () => {
	if (onChooseSource) {
		onChooseSource()
		return
	}
	openPicker(quillRef, { multiple: true })
}

const DESKTOP_TOOLBAR = [
	[{ header: [1, 2, 3, 4, 5, 6, false] }],
	['bold', 'italic', 'underline', 'strike'],
	[{ color: ['#F00', 'yellow', '#0F0', 'green', '#00F', '#000', 'grey'] }],
	[{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
	['link', 'image', 'video'],
	['clean'],
]

const MOBILE_TOOLBAR = [['camera', 'image', 'link']]

/**
 * Build the `modules` prop. Memoise the result at the call site -- passing a
 * fresh object on every render forces react-quill to rebuild the toolbar.
 */
export const buildQuillModules = ({
	isMobile,
	quillRef,
	onChooseImageSource,
}: {
	isMobile: boolean
	quillRef: QuillRef
	/**
	 * Called instead of opening a file picker, so the composer can ask whether
	 * the image is coming from the device or from the library. Must be stable
	 * across renders, or the toolbar is rebuilt on every keystroke.
	 */
	onChooseImageSource?: () => void
}) => ({
	toolbar: {
		container: isMobile ? MOBILE_TOOLBAR : DESKTOP_TOOLBAR,
		handlers: isMobile
			? {
					// The camera stays a direct shot. Standing somewhere with the
					// phone up is not the moment to be asked a question.
					camera: createCameraHandler(quillRef),
					image: createImageHandler(quillRef, onChooseImageSource),
				}
			: { image: createImageHandler(quillRef, onChooseImageSource) },
	},
})

export const QUILL_FORMATS = [
	'header',
	'bold',
	'italic',
	'underline',
	'strike',
	'blockquote',
	'list',
	'bullet',
	'link',
	'indent',
	'image',
	'code-block',
	'color',
]
